/**
 * Adapter for converting AppointmentDto to draggable event data
 * 
 * This adapter creates the data structure used in data-event attributes
 * for drag-and-drop functionality while maintaining compatibility with FullCalendar
 */

import moment from "moment";
import { AppointmentDto, PatientDto, TimeSlotDto, TherapyDto, ScheduleBasedTherapyRxFrequencyDto } from "../../../../../api-client";
import { adaptTherapyFromApi } from "../api-to-client/therapy.adapter";
import { SlotLike, layoutSlots, slotTherapy, soldService } from "../../../../../helpers/slotTherapy";

export interface DraggableEventData {
  duration: string; // HH:mm:ss format
  id: string;
  /**
   * Set by `adaptTimeSlotToDraggableEvent` to mark a per-timeslot shelf drag.
   * The drop handler routes these to the slot-level scheduleUnscheduled verb
   * (split-or-supplement-cleanup + cancellation resolve) instead of the
   * whole-appointment reschedule.
   */
  isSingleTimeSlot?: boolean;
  /**
   * Set on pinboard slot pins created by "Umziehen → Nur diese". The slot is
   * still SCHEDULED in the backend (the move is client-side only), so the drop
   * handler must route it to the atomic slot-level reschedule verb instead of
   * the scheduleUnscheduled verb.
   */
  isScheduledSlotMove?: boolean;
  patient: {
    id: string;
  };
  frequency?: {
    id: string;
    text: string;
    prefferedValue: string;
    /**
     * Carried through because consumers rebuild a TherapyFrequencyDto from this
     * (ContinuousScheduler does, when a dragged event becomes an appointment).
     * They were dropped while `frequency` generated as `object` in the API
     * client — with a real schema there is no reason to lose them.
     */
    minValue: number;
    maxValue: number;
  };
  address?: {
    id: string;
    type: string;
  };
  timeSlots: DraggableTimeSlot[];
}

export interface DraggableTimeSlot {
  appointmentId: string;
  id: string;
  type?: string;
  /**
   * MD-232: the performed LINE ITEM belongs to the SLOT, not to the
   * prescription line. It used to be nested under `therapyRx.therapy`, which no
   * longer mirrors the model — and `slotTherapy()` looks here, so a shelf event
   * carrying it in the old place resolved to no therapy at all. That made the
   * credential check see an empty id list and reject every drag with
   * "Therapeut hat kein Heilmittel für diese Therapie: " and no therapy named.
   */
  therapy: {
    id: string;
    abbreviation: string;
    duration: number;
    maxParticipants?: number;
    /**
     * MD-314: both flags decide WHERE the drop lands, so they have to survive
     * the trip. They did not: the payload carried id/abbreviation/duration
     * only, so every externally dropped slot read as active and non-
     * supplementary. `isPassiveSlot` was therefore false for a Warmpackung,
     * the layout shifted nothing, and the PA took the drop slot while the KG
     * landed on the appointment after it. The API ships both on the slot's
     * line item (appointment.repo.ts selects therapy.isPassive /
     * therapy.isSupplementary); this adapter was the only thing dropping them.
     *
     * On a term-only stub the prescribed term cannot answer it (WAERME
     * resolves to passive HL/PA or active HR depending on the therapist), so
     * it is read from the catalog's default line item per (term, tarif group)
     * — the same winner the stub's duration comes from (MD-550). Absent, not
     * false, when the catalog has no answer either; layoutSlots treats an
     * unknown as active rather than moving the slot on a guess.
     */
    isPassive?: boolean;
    isSupplementary?: boolean;
  };
  therapyRx: {
    id: string;
    rx: {
      rxNumber: string;
      id: string;
      patientId: string;
    };
    amount: number;
    order: number;
  };
}

/**
 * Helper function to convert minutes to HH:mm:ss format
 */
const convertMinutesToTimeString = (minutes: number): string => {
  const duration = moment.duration(minutes, "minutes");
  return moment.utc(duration.asMilliseconds()).format("HH:mm:ss");
};

/**
 * Provisional minutes for a slot that has NO resolved line item yet: the
 * caller-supplied master-data lookup (term × tarif group → default duration,
 * see useTermDefaultDurationLookup). Purely a planning guess — the backend
 * clamps the window to the actually-resolved item's duration at scheduling.
 */
export type SlotDefaultDurationLookup = (
  prescribedTherapyId?: string | null,
  tarifGroupId?: string | null
) => number | undefined;

/**
 * Provisional passivity for a slot that has NO resolved line item yet — the
 * `isPassive` of the same catalog default the duration above comes from (see
 * useTermDefaultPassivityLookup). What lets a Dauertermin template take part in
 * the MD-314 contract before any line item exists (MD-550). `undefined` means
 * the catalog has no answer, and the slot is then laid out as active.
 */
export type SlotDefaultPassivityLookup = (
  prescribedTherapyId?: string | null,
  tarifGroupId?: string | null
) => boolean | undefined;

/** The (term, tarif group) key a term-only slot is looked up by. */
const termKeyOf = (
  timeSlot: TimeSlotDto
): [prescribedTherapyId: string | null, tarifGroupId: string | null] => [
  (timeSlot.therapyRx as any)?.prescribedTherapyId ??
    timeSlot.therapyRx?.prescribedTherapy?.id ??
    null,
  // Both shapes: the shelf payloads carry the loaded relation, the template
  // path the bare FK. Reading only one of them silently yields null, and the
  // lookup can then give no answer at all — see useTermDefaultDurationLookup.
  (timeSlot.therapyRx?.rx as any)?.tarifGroup?.id ??
    (timeSlot.therapyRx?.rx as any)?.tarifGroupId ??
    null,
];

const termDefaultDurationOf = (
  timeSlot: TimeSlotDto,
  lookup?: SlotDefaultDurationLookup
): number | undefined => lookup?.(...termKeyOf(timeSlot));

const termDefaultPassivityOf = (
  timeSlot: TimeSlotDto,
  lookup?: SlotDefaultPassivityLookup
): boolean | undefined => lookup?.(...termKeyOf(timeSlot));

/**
 * Convert AppointmentDto to DraggableEventData for drag-and-drop operations
 */
export const adaptAppointmentToDraggableEvent = (
  appointment: AppointmentDto,
  patient?: PatientDto,
  excludeHeatTreatments = true,
  defaultDurationLookup?: SlotDefaultDurationLookup,
  defaultPassivityLookup?: SlotDefaultPassivityLookup
): DraggableEventData => {

  // Get patient info from parameter or the first timeslot that carries a
  // prescription patient (slot [0] can be a therapy-less group anchor slot).
  const patientInfo =
    patient ||
    (appointment.timeSlots?.find((ts) => ts.therapyRx?.rx?.patient)?.therapyRx?.rx
      ?.patient as PatientDto);

  // Convert timeSlots to draggable format. Skip slots with neither a therapy
  // nor a prescription line to default from — a slot legitimately has none
  // when it is a group anchor's GroupTimeSlotTherapist (no therapyRx at all);
  // feeding undefined into adaptTherapyFromApi crashed the whole calendar with
  // a white screen when a cancellation row pointed at such an anchor
  // appointment. Term-only stubs (therapyRx present, line item not yet
  // resolved) stay in the payload with an id-LESS therapy carrying the
  // master-data default duration: slotTherapy() then lays the drag out with a
  // realistic length, while the id-less therapy is never sent to the API as a
  // line-item request (updateReceivedAppointment drops therapies without id).
  //
  // MD-724: a sale has no line item and no term; its sold service is what it
  // delivers. Without it the sale took the stub branch below and a mouse drag
  // wrote 30 minutes where "b" (layoutTherapy → soldService) wrote the
  // service's own duration.
  const draggableTimeSlots: DraggableTimeSlot[] = appointment.timeSlots
    ?.filter(
      (timeSlot: TimeSlotDto) =>
        !!timeSlot.therapy || !!timeSlot.therapyRx || !!soldService(timeSlot)
    )
    .map((timeSlot: TimeSlotDto) => {
    const therapy = (timeSlot.therapy ?? soldService(timeSlot)) as TherapyDto;
    const clientTherapy = therapy ? adaptTherapyFromApi(therapy) : undefined;

    return {
      appointmentId: appointment.id!,
      id: timeSlot.id!,
      type: timeSlot.type,
      // MD-232: on the slot, matching the model and what slotTherapy() reads.
      therapy: clientTherapy
        ? {
            id: clientTherapy.id,
            abbreviation: clientTherapy.abbreviation,
            duration: clientTherapy.duration,
            maxParticipants: (therapy as any).maxParticipants,
            // MD-314: straight off the resolved line item, no inference.
            isPassive: (therapy as any).isPassive === true,
            isSupplementary: (therapy as any).isSupplementary === true,
          }
        : {
            id: "",
            abbreviation:
              timeSlot.therapyRx?.prescribedTherapy?.shortLabel ??
              (timeSlot.therapyRx as any)?.prescribedTherapyId ??
              "",
            duration: termDefaultDurationOf(timeSlot, defaultDurationLookup) ?? 30,
            // A term DOES carry supplementary-ness, so ordering still works on a
            // stub. Passivity it does not carry; the catalog's default line item
            // for (term, tarif group) answers it — the same winner the duration
            // above comes from (MD-550) — and when it has no answer `isPassive`
            // stays absent rather than guessed — see the field's comment.
            ...(() => {
              const isPassive = termDefaultPassivityOf(timeSlot, defaultPassivityLookup);
              return isPassive === undefined ? {} : { isPassive };
            })(),
            isSupplementary: timeSlot.therapyRx?.prescribedTherapy?.isSupplementary === true,
          },
      therapyRx: {
        id: timeSlot.therapyRx?.id!,
        rx: {
          rxNumber: timeSlot.therapyRx?.rx?.rxNumber!,
          id: timeSlot.therapyRx?.rx?.id!,
          patientId: String(patientInfo?.id!),
        },
        amount: timeSlot.therapyRx?.amount!,
        order: timeSlot.therapyRx?.order!,
      },
    };
  }) || [];

  // The ghost's length is how much time the drop claims AT the drop point, so
  // it is derived from the SAME layout the drop will apply (MD-314) — and from
  // the finished payload, not the source DTO, so the two cannot drift apart.
  // Passive preparations run ahead of the drop point and are therefore not part
  // of it; `excludeHeatTreatments = false` asks for the appointment's full span
  // instead.
  // The cast is structural only: DraggableTimeSlot differs from SlotLike in
  // `therapyRx.rx.patientId` (string here, number there) — a field the layout
  // never reads. Everything it does read (therapy, therapyRx.order) matches.
  const { atDropPoint } = layoutSlots(draggableTimeSlots as unknown as SlotLike[]);
  const ghostSlots = excludeHeatTreatments
    ? (atDropPoint as unknown as DraggableTimeSlot[])
    : draggableTimeSlots;
  const totalDuration = ghostSlots.reduce(
    (acc, slot) => acc + (Number(slot.therapy?.duration) || 0),
    0
  );

  const draggableEvent: DraggableEventData = {
    duration: convertMinutesToTimeString(totalDuration),
    id: appointment.id!,
    patient: {
      id: String(patientInfo?.id!),
    },
    timeSlots: draggableTimeSlots,
  };

  // Add optional fields if they exist
  if (appointment.frequency) {
    // No longer `as any`: TherapyFrequencyDto is a real schema now, so the
    // bounds are typed and can be carried instead of silently dropped.
    const frequency = appointment.frequency;
    draggableEvent.frequency = {
      id: String(frequency.id || ''),
      text: frequency.text || '',
      prefferedValue: String(frequency.prefferedValue ?? ''),
      minValue: frequency.minValue,
      maxValue: frequency.maxValue,
    };
  }

  if (appointment.address) {
    draggableEvent.address = {
      id: String(appointment.address.id!),
      type: appointment.address.type!,
    };
  }

  return draggableEvent;
};

/**
 * Convert a SINGLE timeSlot of an appointment into DraggableEventData.
 *
 * Used by the shelf's per-timeslot drag handles so that dropping an individual
 * therapy slot carries only that slot (and its own duration) instead of the
 * whole appointment. Patient / frequency / address metadata is inherited from
 * the parent appointment so the drop handler behaves like the whole-appointment
 * drag in every other respect.
 */
export const adaptTimeSlotToDraggableEvent = (
  appointment: AppointmentDto,
  timeSlot: TimeSlotDto,
  patient?: PatientDto,
  defaultDurationLookup?: SlotDefaultDurationLookup,
  defaultPassivityLookup?: SlotDefaultPassivityLookup
): DraggableEventData => {
  const patientInfo = patient || (appointment.timeSlots?.[0]?.therapyRx?.rx?.patient as PatientDto);

  const therapy = slotTherapy(timeSlot) as TherapyDto;
  const clientTherapy = adaptTherapyFromApi(therapy);
  // adaptTherapyFromApi hard-defaults a missing duration to 30 — for a
  // term-only stub the master-data default (the term's resolution winner in
  // this tarif group) is the better provisional length.
  const provisionalDuration =
    (therapy as any)?.duration != null
      ? clientTherapy.duration
      : termDefaultDurationOf(timeSlot, defaultDurationLookup) ?? clientTherapy.duration;

  const draggableTimeSlot: DraggableTimeSlot = {
    appointmentId: appointment.id!,
    id: timeSlot.id!,
    type: timeSlot.type,
    // MD-232: on the slot, matching the model and what slotTherapy() reads.
    therapy: {
      id: clientTherapy.id,
      abbreviation: clientTherapy.abbreviation,
      duration: provisionalDuration,
      maxParticipants: (therapy as any)?.maxParticipants,
      // MD-314: carried for the same reason as in the whole-appointment
      // adapter. A single slot never shifts on its own (there is no main
      // therapy to lead into), but a slot pin dropped back next to its
      // siblings is laid out with them. A resolved line item speaks first; a
      // term-only slot falls back to the catalog default (MD-550), then to
      // the term's own entry-abbreviation stopgap that slotTherapy supplies.
      ...(() => {
        const isPassive =
          (therapy as any)?.id
            ? (therapy as any)?.isPassive
            : termDefaultPassivityOf(timeSlot, defaultPassivityLookup) ??
              (therapy as any)?.isPassive;
        return isPassive !== undefined ? { isPassive: isPassive === true } : {};
      })(),
      isSupplementary:
        (therapy as any)?.isSupplementary === true ||
        timeSlot.therapyRx?.prescribedTherapy?.isSupplementary === true,
    },
    therapyRx: {
      id: timeSlot.therapyRx?.id!,
      rx: {
        rxNumber: timeSlot.therapyRx?.rx?.rxNumber!,
        id: timeSlot.therapyRx?.rx?.id!,
        patientId: String(patientInfo?.id!),
      },
      // MD-232: open-ended lines (Langzeit / Blanko plan lines) carry no
      // authorized amount; 0 means "no fixed quota" to the drag layer.
      amount: timeSlot.therapyRx?.amount ?? 0,
      order: timeSlot.therapyRx?.order!,
    },
  };

  const draggableEvent: DraggableEventData = {
    duration: convertMinutesToTimeString(provisionalDuration),
    id: appointment.id!,
    isSingleTimeSlot: true,
    patient: {
      id: String(patientInfo?.id!),
    },
    timeSlots: [draggableTimeSlot],
  };

  if (appointment.frequency) {
    const frequency = appointment.frequency;
    draggableEvent.frequency = {
      id: String(frequency.id || ''),
      text: frequency.text || '',
      prefferedValue: String(frequency.prefferedValue ?? ''),
      minValue: frequency.minValue,
      maxValue: frequency.maxValue,
    };
  }

  if (appointment.address) {
    draggableEvent.address = {
      id: String(appointment.address.id!),
      type: appointment.address.type!,
    };
  }

  return draggableEvent;
};